﻿import { NgService } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { IFieldUploadOptions, IRootScope } from "@models";
import * as angular from "angular";

@NgService({
    token: Token.UploadService,
    dependencies: [
        Token.$Q,
        Token.$Http,
        Token.$RootScope,
    ]
})
export class UploadService {

    constructor(
        private $q: ng.IQService,
        private $http: ng.IHttpService,
        private $rootScope: IRootScope
    ) {
        this.uploadInputHelper = angular.element('<input type="file" />');

    }

    public promptUserToChooseFile(accept?: string): ng.IPromise<File> {
        let deferred = this.$q.defer<File>();
        this.uploadInputHelper = angular.element('<input type="file" />');
        if (this.currentCallback) this.uploadInputHelper.unbind("change", this.currentCallback);
        this.uploadInputHelper.val(null); //clear the value otherwise the change event won't fire if another file is picked
        if (accept) {
            this.uploadInputHelper.attr("accept", accept);
        } else {
            this.uploadInputHelper.removeAttr("accept");
        }
        this.currentCallback = e => {
            let element: any = e.target;
            this.uploadInputHelper[0].removeEventListener("change", this.currentCallback);
            this.currentCallback = null;
            if (!element || element.files.length == 0) {
                deferred.reject();
                return;
            }
            let file = element.files[0];
            file.fullPath = this.uploadInputHelper.val();
            deferred.resolve(file);
        };
        this.uploadInputHelper[0].addEventListener("change", this.currentCallback);
        this.uploadInputHelper[0].click();
        return deferred.promise;
    }

    public promptUserToChooseFiles(): ng.IPromise<File[]> {
        let deferred = this.$q.defer<File[]>();
        this.uploadInputHelper = angular.element('<input type="file" />');
        this.uploadInputHelper.attr("multiple", "multiple");
        if (this.currentCallback) this.uploadInputHelper.unbind("change", this.currentCallback);
        this.uploadInputHelper.val(null); //clear the value otherwise the change event won't fire if another file is picked

        this.currentCallback = e => {
            this.uploadInputHelper.removeAttr("multiple");
            let element: any = e.target;
            this.uploadInputHelper.unbind("change", this.currentCallback);
            this.currentCallback = null;
            if (!element || element.files.length == 0) {
                deferred.reject();
                return;
            }
            let val = this.uploadInputHelper.val();
            let files = [];
            for (let f of element.files) {
                f.fullPath = val;
                files.push(f);
            } 
            deferred.resolve(files);
        };
        this.uploadInputHelper.bind("change", this.currentCallback);
        this.uploadInputHelper.click();
        return deferred.promise;

    }

    public uploadAttachment(file: File, entity, url): ng.IPromise<any> {
        let postConfig: ng.IRequestShortcutConfig = {
            // add this to not serialize form data
            transformRequest: angular.identity,
            // set header to undefined so the browser sets the multipart content type
            headers: { "Content-Type": undefined }
        };
        let deferred = this.$q.defer();
        let formData = new FormData();
        formData.append("file", file);
        formData.append("json", JSON.stringify(entity));
        this.$http.post(url, formData, postConfig).then(response => {
            // returns the filename of the new node in the upload scene
            let uploadResult = response.data as any;
            deferred.resolve(uploadResult);
        }, reason => {
            deferred.reject(reason);
        });
        return deferred.promise;
    }

    public uploadFileToMedia(file: File, dir: string, apiPath: string = "api/media"): ng.IPromise<any> {
        let d = this.$q.defer();
        let xhr = new XMLHttpRequest();
        let formData = new FormData();
        formData.append("file", file);
        formData.append("dir", dir);
        xhr.onreadystatechange = (e => {
            if (xhr.readyState == 4) {
                if (xhr.status == 200) {
                    d.resolve();
                } else {
                    d.reject();
                }
            }
        });
        // xhr.upload.onload = d.resolve;
        xhr.upload.onprogress = d.notify;
        // xhr.addEventListener("error", d.reject);
        xhr.addEventListener("abort", d.reject, false);
        xhr.open("POST", apiPath + "/upload", true);
        // xhr.setRequestHeader("Content-Type", "multipart/form-data");
        // xhr.setRequestHeader("x-ms-version", "2014-02-14");
        // xhr.setRequestHeader("x-ms-date",(new Date()).toISOString());
        // TODO: kbmax3dev is hardcoded. Need a way to determine the correct account to use (maybe Web.config?)
        // xhr.setRequestHeader("Authentication", "SharedKey kbmax3dev:" + signature);
        // xhr.setRequestHeader("x-ms-blob-type", "BlockBlob");
        xhr.send(formData);
        return d.promise;
    }

    public uploadProfileImage(file: File, userId: number): ng.IPromise<any> {
        let url = "/api/users/profileimage";
        let postConfig: ng.IRequestShortcutConfig = {
            // add this to not serialize form data
            transformRequest: angular.identity,
             // set header to undefined so the browser sets the multipart content type
            headers: { "Content-Type": undefined }
        };
        let formData = new FormData();
        formData.append("userId", userId.toString());
        formData.append("file", file, file.name);

        let deferred = this.$q.defer();
        // post the file
        this.$http.put(url, formData, postConfig).then(response => {
            // returns the filename of the new node in the upload scene
            let uploadResult = response.data as any;
            deferred.resolve(uploadResult.imagePath);
        }, reason => {
            deferred.reject(reason);
        });

        return deferred.promise;
    }

    public importConfigurator(file: File): ng.IPromise<any> {
        var formData = new FormData();
        formData.append("file", file);
        return this.uploadFile(formData, "api/admin/products/import");
    }

    public uploadConfiguredProductFile(file: File, options: IFieldUploadOptions = {convertPdfToImage: false}): ng.IPromise<any> {
        var formData = new FormData();
        formData.append("file", file);
        formData.append("convertPdfToImage", options.convertPdfToImage.toString());
        return this.uploadFile(formData, "api/quotes/filefromconfiguratorfield");       
    }

    public uploadQuoteFile(file: File, options: IFieldUploadOptions = {convertPdfToImage: false}): ng.IPromise<any> {
        var formData = new FormData();
        formData.append("file", file);
        formData.append("convertPdfToImage", options.convertPdfToImage.toString());
        return this.uploadFile(formData, "api/quotes/filefromquoteheaderfield");
    }

    public uploadFile(formData: FormData, url: string): ng.IPromise<any> {
        let postConfig: ng.IRequestShortcutConfig = {
            // add this to not serialize form data
            transformRequest: angular.identity,
            // set header to undefined so the browser sets the multipart content type
            headers: { "Content-Type": undefined },
            tracker: this.$rootScope.contentTracker
        };
        let deferred = this.$q.defer();
        let xhr = new XMLHttpRequest();
        this.$http.post(url, formData, postConfig).then(response => {
            // returns the filename of the new node in the upload scene
            let uploadResult = response.data as any;
            deferred.resolve(uploadResult);
        }, reason => {
            deferred.reject(reason);
        });
        return deferred.promise;
    }

    private currentCallback: (e) => void;
    private uploadInputHelper: JQuery;

}
